import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import axios from 'axios';

type GlobalWithResizeObserverPatch = typeof globalThis & {
  __ep3dResizeObserverPatched?: boolean;
};

const patchResizeObserverWithRaf = () => {
  const runtime = globalThis as GlobalWithResizeObserverPatch;
  if (runtime.__ep3dResizeObserverPatched || !globalThis.ResizeObserver) {
    return;
  }

  const NativeResizeObserver = globalThis.ResizeObserver;

  class RafResizeObserver implements ResizeObserver {
    private readonly observer: ResizeObserver;

    constructor(callback: ResizeObserverCallback) {
      this.observer = new NativeResizeObserver((entries, obs) => {
        globalThis.requestAnimationFrame(() => callback(entries, obs));
      });
    }

    observe(target: Element, options?: ResizeObserverOptions): void {
      this.observer.observe(target, options);
    }

    unobserve(target: Element): void {
      this.observer.unobserve(target);
    }

    disconnect(): void {
      this.observer.disconnect();
    }
  }

  globalThis.ResizeObserver = RafResizeObserver as unknown as typeof ResizeObserver;
  runtime.__ep3dResizeObserverPatched = true;
};

patchResizeObserverWithRaf();

const isResizeObserverLoopWarning = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.includes('ResizeObserver loop completed with undelivered notifications')
      || value.includes('ResizeObserver loop limit exceeded');
  }

  if (value && typeof value === 'object' && 'message' in value) {
    return isResizeObserverLoopWarning((value as { message?: unknown }).message);
  }

  return false;
};

if (process.env.NODE_ENV === 'development') {
  globalThis.addEventListener(
    'error',
    (event) => {
      if (!isResizeObserverLoopWarning(event.message) && !isResizeObserverLoopWarning(event.error)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  globalThis.addEventListener(
    'unhandledrejection',
    (event) => {
      if (!isResizeObserverLoopWarning(event.reason)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (args.some(isResizeObserverLoopWarning)) {
      return;
    }

    originalConsoleError(...args);
  };
}

//create axios interceptor to customize requests
axios.interceptors.request.use((request) => {
  let pathname = globalThis.location.pathname;
  if(pathname === '/unAuthorized')
      pathname=""
  //pass project name only if not present
  if (!request.headers.projectName) {
    if (pathname === '/') {
      request.headers.projectName = 'home';
    } else {
      let path = pathname.split('/')[1];
      request.headers.projectName = path;
    }
  }
  return request;
});

ReactDOM.render(<App />, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
